import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="file-upload"
// Usage:
//   data-controller="file-upload"
//   data-file-upload-allowed-types-value='["application/pdf","image/jpeg","image/png"]'
//
// If no allowed types are specified, defaults to PDF only.
//
export default class extends Controller {
  static targets = ["fileInput", "previewArea", "fileList"]
  static values = { allowedTypes: Array }

  connect() {
    // Ensure we always have a sane array of allowed types
    if (!this.hasAllowedTypesValue || !Array.isArray(this.allowedTypesValue) || this.allowedTypesValue.length === 0) {
      this.allowedTypesValue = ["application/pdf", "text/plain"]
    }

    this.setupDragAndDrop()
  }

  disconnect() {
    this.teardownDragAndDrop()
  }

  setupDragAndDrop() {
    // Bind once so we can properly remove listeners later
    this.boundPreventDefaults = this.boundPreventDefaults || this.preventDefaults.bind(this)

    document.addEventListener("dragover", this.boundPreventDefaults)
    document.addEventListener("drop", this.boundPreventDefaults)
  }

  teardownDragAndDrop() {
    if (!this.boundPreventDefaults) return

    document.removeEventListener("dragover", this.boundPreventDefaults)
    document.removeEventListener("drop", this.boundPreventDefaults)
  }

  preventDefaults(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  handleFileSelect(event) {
    const files = Array.from(event.target.files || [])
    this.processFiles(files)
  }

  handleDragOver(event) {
    this.preventDefaults(event)
    this.toggleHighlight(true)
  }

  handleDragLeave(event) {
    this.preventDefaults(event)

    // Only remove highlight if we're actually leaving the drop zone
    if (!this.element.contains(event.relatedTarget)) {
      this.toggleHighlight(false)
    }
  }

  handleDrop(event) {
    this.preventDefaults(event)
    this.toggleHighlight(false)

    const files = Array.from(event.dataTransfer?.files || [])
    this.processFiles(files)
    this.updateFileInput(files)
  }

  processFiles(files) {
    if (!files.length) return

    const allowedFiles = files.filter((file) => this.allowedTypesValue.includes(file.type))
    const rejectedFiles = files.filter((file) => !this.allowedTypesValue.includes(file.type))

    if (rejectedFiles.length > 0) {
      const typeNames = this.getFileTypeNames(this.allowedTypesValue)
      this.showFileError(
        `Only ${typeNames} files are allowed. Rejected: ${rejectedFiles.map((f) => f.name).join(", ")}`
      )
    }

    if (allowedFiles.length > 0) {
      this.displayFiles(allowedFiles)
      this.showPreviewArea()
    }
  }

  updateFileInput(files) {
    const dt = new DataTransfer()

    files.forEach((file) => {
      if (this.allowedTypesValue.includes(file.type)) {
        dt.items.add(file)
      }
    })
    this.fileInputTarget.files = dt.files
  }

  displayFiles(files) {
    this.fileListTarget.innerHTML = ""

    files.forEach((file, index) => {
      const fileItem = this.createFileItem(file, index)
      this.fileListTarget.appendChild(fileItem)
    })
  }

  createFileItem(file, _index) {
    const fileItem = document.createElement("div")
    fileItem.className = "p-3 bg-base-100 rounded-lg border"
    fileItem.dataset.fileName = file.name

    const fileSize = this.formatFileSize(file.size)
    const truncatedName = this.truncateFilename(file.name, 40)

    fileItem.innerHTML = `
      <div class="flex items-center justify-between gap-3 mb-2">
        <div class="flex items-center space-x-3 min-w-0 flex-1">
          <svg class="size-8 text-error flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
          </svg>
          <div class="min-w-0 flex-1">
            <p class="font-medium text-sm truncate" title="${this.escapeHtml(file.name)}">
              ${this.escapeHtml(truncatedName)}
            </p>
            <p class="text-xs text-base-content opacity-70">${fileSize}</p>
          </div>
        </div>
        <button
          type="button"
          class="btn btn-ghost btn-sm btn-circle flex-shrink-0"
          data-action="click->file-upload#removeFile"
          data-file-name="${this.escapeHtml(file.name)}"
          data-file-size="${file.size}"
        >
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      <div class="upload-status-area"></div>
    `

    return fileItem
  }

  removeFile(event) {
    const fileName = event.currentTarget.dataset.fileName
    const fileSize = parseInt(event.currentTarget.dataset.fileSize, 10)

    const currentFiles = Array.from(this.fileInputTarget.files)

    const fileIndex = currentFiles.findIndex(
      (file) => file.name === fileName && file.size === fileSize
    )

    if (fileIndex === -1) return

    currentFiles.splice(fileIndex, 1)

    const dt = new DataTransfer()
    currentFiles.forEach((file) => dt.items.add(file))
    this.fileInputTarget.files = dt.files

    if (currentFiles.length > 0) {
      this.displayFiles(currentFiles)
    } else {
      this.hidePreviewArea()
    }
  }

  toggleHighlight(active) {
    this.element.classList.toggle("border-primary", active)
    this.element.classList.toggle("bg-primary", active)
    this.element.classList.toggle("bg-opacity-10", active)
    this.element.classList.toggle("border-base-300", !active)
  }

  showPreviewArea() {
    this.previewAreaTarget.classList.remove("hidden")
  }

  hidePreviewArea() {
    this.previewAreaTarget.classList.add("hidden")
    this.fileListTarget.innerHTML = ""
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"

    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  truncateFilename(filename, maxLength = 40) {
    if (filename.length <= maxLength) return filename

    const lastDotIndex = filename.lastIndexOf(".")
    if (lastDotIndex === -1) {
      return filename.substring(0, maxLength - 3) + "..."
    }

    const extension = filename.substring(lastDotIndex)
    const nameWithoutExtension = filename.substring(0, lastDotIndex)
    const availableLength = maxLength - extension.length - 3

    if (availableLength <= 0) {
      return filename.substring(0, maxLength - 3) + "..."
    }

    return nameWithoutExtension.substring(0, availableLength) + "..." + extension
  }

  getFileTypeNames(mimeTypes) {
    const typeMap = {
      "application/pdf": "PDF",
      "image/jpeg": "JPEG",
      "image/jpg": "JPG",
      "image/png": "PNG",
      "image/gif": "GIF",
      "image/webp": "WebP",
      "text/plain": "Text",
      "text/csv": "CSV",
      "application/msword": "Word",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
      "application/vnd.ms-excel": "Excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
    }

    const names = mimeTypes.map((type) => typeMap[type] || type.split("/")[1]?.toUpperCase() || type)

    if (names.length === 1) return names[0]
    if (names.length === 2) return names.join(" and ")
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`
  }

  showFileError(message) {
    let errorDiv = this.element.querySelector(".file-error-message")

    if (!errorDiv) {
      errorDiv = document.createElement("div")
      errorDiv.className = "alert alert-warning file-error-message mt-4 flex items-center gap-2"
      this.element.appendChild(errorDiv)
    }

    errorDiv.innerHTML = `
      <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
      </svg>
      ${this.escapeHtml(message)}
    `

    setTimeout(() => {
      if (errorDiv) errorDiv.remove()
    }, 4000)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}